import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * UserMappingPage Component
 * 
 * Displays user mapping tools content like the legacy HTML site
 */
const UserMappingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // User Mapping intro content from the legacy HTML
  const userMappingIntroHtml = `
    <p>
    Once we have solid actionable research, user insights and a better understanding of the problem at hand, we can begin to map this data in a way that helps further visualise the user themselves, and the issue they are facing.
    </p>

    <p>
    By understanding the various aspects of the user's existing experience we are starting to set ourselves up for the ideation phase.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const personaTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/user-mapping/persona.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koNA_gk=/" className="link-table">Miro</a>
    </>
  );

  const empathyMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/user-mapping/empathy-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koM0lHM=/" className="link-table">Miro</a>
    </>
  );

  const customerJourneyMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/user-mapping/customer-journey-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koMJTZw=/" className="link-table">Miro</a>
    </>
  );

  const experienceMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/user-mapping/experience-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koM9mDE=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const alanCooperCreator = <a href="https://en.wikipedia.org/wiki/Persona_(user_experience)">Alan Cooper</a>;
  const daveGrayCreator = <a href="https://www.solutionsiq.com/resource/blog-post/what-is-an-empathy-map/">Dave Gray</a>;
  const colinShawCreator = <a href="https://www.oreilly.com/library/view/mapping-experiences/9781491923528/maex_ch10.xhtml">Colin Shaw</a>;
  const naCreator = <a href="#">N/A</a>;

  return (
    <>
      {/* User Mapping Intro */}
      <div id="section-tools-user-mapping" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#mapping">Mapping</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/mapping/user-mapping">User Mapping</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={userMappingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#persona">Persona</Link></div>
            <div><Link className="tool-section-cta" to="#empathy-map">Empathy Map</Link></div>
            <div><Link className="tool-section-cta" to="#customer-journey-map">Customer Journey Map</Link></div>
            <div><Link className="tool-section-cta" to="#experience-map">Experience Map</Link></div>
          </div>
        </div>
      </div>

      {/* Persona Section */}
      <main className="case-study">
        <span className="case-tool-title" id="persona">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="persona">
            <span className="tool-title-number">
              <Link to="/research/mapping/user-mapping#persona">Persona</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/mapping/user-mapping">User Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/user-mapping#persona"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-persona" 
            id="tab-persona-overview" 
            defaultChecked
          />
          <label htmlFor="tab-persona-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Mapping", "24/08/20", personaTemplates, alanCooperCreator)}
              <div className="tool-opening">
                <p>
                A Persona is a typical example of a user, they act as a representation of a users' goal and are created as a means of building empathy with a user and connecting it with an overarching purpose. By creating them, we can not only use them to understand the users, but also the purpose of the product and business and ultimately helps focus the design to solve the user problem. 
                </p>
                
                <p>
                The Persona is built up of the relevant elements of the project, along with trends that have been identified during rebased and based on an audience, not a specific person. It will capture the audience motivations, the frustrations they face and the core soul of the audience. It is important not to use generalisations or stereotypes, and to update regularly throughout the project. 
                </p>
                
                <p>
                Along with a Persona template, they are also used in Problem Statements, User Stories and How Might We's. We can compile them from key quotes and other insights gathered during the research phase.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxplanet.org/persona-versus-proto-persona-9e26e831ed51" className="link-table">Persona versus Proto-Persona</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://diytoolkit.org/tools/personas/" className="link-table">Personas - DIY Toolkit</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-persona" 
            id="tab-persona-template"
          />
          <label htmlFor="tab-persona-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koNA_gk=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Persona Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Empathy Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="empathy-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="empathy-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/user-mapping#empathy-map">Empathy Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/mapping/user-mapping">User Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/user-mapping#empathy-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-empathy-map" 
            id="tab-empathy-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-empathy-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Mapping", "28/08/20", empathyMapTemplates, daveGrayCreator)}
              <div className="tool-opening">
                <p>
                As the name suggests, Empathy Maps help us to build empathy with our users, understanding their thoughts and actions, and turning them into actionable insights. 
                </p>
                
                <p>
                They are particularly helpful when sharing knowledge about the users to the team, helping to create a common understanding and shared user perspective.  
                </p>
                
                <p>
                They can also uncover missing information or research that needs to be filled in and uncover user needs, whilst guiding us towards constant innovation by revisiting them regularly and going deeper with understanding.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.ibm.com/design/thinking/page/toolkit/activity/empathy-map" className="link-table">Enterprise Design Thinking - Empathy Map</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/empathy-mapping/" className="link-table">Empathy Mapping: The First Step in Design Thinking</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/empathy-map-why-and-how-to-use-it" className="link-table">Empathy Map – Why and How to Use It</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://boagworld.com/usability/adapting-empathy-maps-for-ux-design/" className="link-table">Adapting empathy maps for UX design</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-empathy-map" 
            id="tab-empathy-map-template"
          />
          <label htmlFor="tab-empathy-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koM0lHM=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Empathy Map Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Customer Journey Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="customer-journey-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="customer-journey-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/user-mapping#customer-journey-map">Customer Journey Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/mapping/user-mapping">User Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/user-mapping#customer-journey-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-customer-journey-map" 
            id="tab-customer-journey-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-customer-journey-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Mapping", "28/08/20", customerJourneyMapTemplates, colinShawCreator)}
              <div className="tool-opening">
                <p>
                A Customer Journey Map is a visualisation of a process that a user follows in order to achieve a goal, with a specific business, service or product.
                </p>
                
                <p>
                By following this process, it helps to understand what a user goes through over the period of time trying to reach that goal, surfacing the needs and pain points of the user.
                </p>
                
                <p>
                Derived from research, including usability, it is plotted by understanding the various touchpoint a user might have within the journey - these touchpoints will expose problems and therefore opportunities for further investigation, it will also surface potentially new customer segments. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/analyze-customer-journey-map/" className="link-table">7 Ways to Analyze a Customer-Journey Map</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://blog.hubspot.com/service/customer-journey-map" className="link-table">How to Create an Effective Customer Journey Map</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-customer-journey-map" 
            id="tab-customer-journey-map-template"
          />
          <label htmlFor="tab-customer-journey-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koMJTZw=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Customer Journey Map Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Experience Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="experience-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="experience-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/user-mapping#experience-map">Experience Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/mapping/user-mapping">User Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/user-mapping#experience-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-experience-map" 
            id="tab-experience-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-experience-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Mapping", "28/08/20", experienceMapTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Experience Maps are a visual representation of the end to end experience a user goes through currently when trying to achieve their goal outside of a Product. 
                </p>
                
                <p>
                It helps with understanding the user and their experience of a particular scenario by understanding the cause from the user's point of view.
                </p>
                
                <p>
                Consisting of 3 core areas, an Experience Map will focus on the;
                </p>

                <ul className="bullet-tk">
                  <li>User Journey through the main stages towards their goal considering the User's actions inside and outside the product (online and offline) from their point of view.</li>
                  <li>Analyse the Task they are trying to achieve by walking through their steps ourselves, using user interview insights including usability testing and observing users contextually.</li>
                  <li>and understand the user's Emotional state as they proceed through their goal. User thoughts, feelings, motivations, reactions, attitude, moods and general attention can be included.</li>
                </ul>
                
                <p>
                The Experience Map itself is dependent on the project, so you can include a number of things that help visualise things, for example you can also use the experience map to tie the user and business together by including a SWOT analysis. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://productcoalition.com/experience-maps-449a9c331042" className="link-table">Experience Maps</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.gov.uk/service-manual/user-research/creating-an-experience-map" className="link-table">Creating an experience map</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxdesign.cc/developing-experience-map-at-ai-startup-c402840bfd6" className="link-table">Developing an experience map, A to Z.</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.newhaircut.com/resource-library/problem-framing-v2-part-4-of-4" className="link-table">Problem Framing v2: Part 4 of 4</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-experience-map" 
            id="tab-experience-map-template"
          />
          <label htmlFor="tab-experience-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koM9mDE=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Experience Map Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserMappingPage; 